import { Sparkles } from "lucide-react";

/**
 * Dark luxury brand-story panel. Founder voice with a handwritten
 * signature accent — the emotional anchor of the homepage. Three pill
 * badges sit subordinate to the copy (no longer the headline).
 *
 * Jun 2026 — Free-delivery threshold now reads from Settings >
 * `free_delivery_threshold` so the badge stays in sync with the rest
 * of the site (footer, cart progress, product detail).
 */
export default function BrandStorySection() {
    return (
        <section
            data-testid="shop-brand-story"
            className="mb-12 lg:mb-24 -mx-4 sm:-mx-6 lg:-mx-8"
        >
            <div className="relative bg-ink text-ivory overflow-hidden lg:rounded-3xl lg:mx-4 sm:lg:mx-6 lg:mx-8">
                {/* Decorative gold radial */}
                <div
                    className="absolute -top-20 -left-20 w-[440px] h-[440px] opacity-20 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(circle, rgba(201,164,122,0.7), transparent 70%)",
                    }}
                />
                <div
                    className="absolute -bottom-32 -right-20 w-[520px] h-[520px] opacity-15 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(circle, rgba(127,29,55,0.85), transparent 70%)",
                    }}
                />

                {/* Phase 2.1 (Jul 2026) — the v2 trim narrowed the CONTENT but
                    left the dark canvas full-bleed → dead space either side on
                    wide screens (owner catch). Desktop now composes the canvas:
                    story column left, the founder's promise elevated to a large
                    typographic feature right — the quote IS the visual. Mobile
                    keeps the single narrow column. */}
                <div className="relative max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-10 lg:py-16 grid lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-16 items-center">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.32em] text-rosegold font-bold mb-3 flex items-center gap-3">
                            <Sparkles size={11} />
                            Our story
                        </p>
                        <h3 className="font-display text-3xl sm:text-4xl leading-[1.05] mb-4 font-light">
                            Hand-picked, <span className="italic text-rosegold">every single</span> piece.
                        </h3>
                        <p className="text-sm sm:text-base text-ivory/85 leading-relaxed max-w-lg mb-4">
                            Boutique-quality Indian wear, sourced directly from artisans —
                            every piece personally selected for fabric, fit and finish.
                        </p>
                        {/* Mobile keeps the quote inline; on lg it moves to the
                            feature column so the panel reads as one composition */}
                        <p className="lg:hidden text-sm text-ivory/70 leading-relaxed max-w-lg italic font-display">
                            "If I wouldn't wear it myself, it doesn't go on the website."
                        </p>
                        {/* Handwritten-feel signature */}
                        <div className="mt-5 flex items-center gap-3">
                            <SignatureMark />
                            <div>
                                <p className="font-display text-xl text-rosegold leading-none">— Sunali</p>
                                <p className="text-[10px] uppercase tracking-[0.22em] text-stone-400 mt-1">
                                    Founder, JnS Creation
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Desktop feature quote — oversized display serif with a
                        rosegold quotation flourish; fills the right canvas with
                        the brand's single most trust-building sentence. */}
                    <div className="hidden lg:block relative pl-10" data-testid="story-feature-quote">
                        <span aria-hidden className="absolute -top-8 left-0 font-display text-[7rem] leading-none text-rosegold/30 select-none">&ldquo;</span>
                        <p className="font-display italic font-light text-[2rem] xl:text-[2.4rem] leading-[1.25] text-ivory/95">
                            If I wouldn&rsquo;t wear it myself, it doesn&rsquo;t go on the website.
                        </p>
                        <span className="mt-6 block w-16 h-px bg-rosegold/50" aria-hidden />
                    </div>
                </div>
            </div>
        </section>
    );
}

/**
 * Hand-drawn signature flourish. Pure inline SVG so it always lines up
 * crisply with the "— Sunali" type without needing an asset.
 */
function SignatureMark() {
    return (
        <svg
            width="48"
            height="40"
            viewBox="0 0 48 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className="text-rosegold/70 flex-shrink-0"
        >
            <path
                d="M4 28 C 8 18, 14 14, 20 18 C 24 21, 22 28, 16 30 C 10 32, 6 26, 14 22 C 22 18, 30 24, 34 28 C 38 32, 42 28, 44 22"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
            />
            <circle cx="44" cy="22" r="1.4" fill="currentColor" />
        </svg>
    );
}
